import { Injectable, inject } from '@angular/core';
import { deleteCascadingFieldsIfNecessary, MercerData } from '@coin/shared/core/util-models';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MercerJobCodeSearchablePropertyEnum } from '@coin/shared/master-data/util';
import { MercerDataService } from './mercer-data.service';

@Injectable({
  providedIn: 'root'
})
export class MercerValidationService {
  private readonly mercerDataService = inject(MercerDataService);

  public getAllFieldValuesAndEnsureMercerCorrectness(
    selectedKey: string,
    mercerData: MercerData
  ): Observable<Record<string, string[]>> {
    const maxSize = 100000;
    const keys = Object.keys(mercerData).filter(
      mercerKey =>
        mercerKey !== selectedKey &&
        mercerKey !== 'positionClass' &&
        mercerKey !== 'jobTitle' &&
        !mercerKey?.includes('Description') &&
        selectedKey !== 'jobCode' &&
        mercerKey !== 'id' &&
        mercerKey !== 'originId'
    );
    const keyOrder = [
      'jobCode',
      'familyCode',
      'family',
      'subFamilyCode',
      'subFamily',
      'specializationCode',
      'specialization',
      'careerStream',
      'careerLevel',
      'careerLevelShort'
    ];

    return this.mercerDataService.getMercerJobProfileFieldValues(maxSize, keys, mercerData).pipe(
      tap(data => {
        Object.entries(data).forEach(([mercerKey, content]) => {
          const isBelowSelectedKey = keyOrder.indexOf(mercerKey) > keyOrder.indexOf(selectedKey);
          if (!content?.includes(mercerData[mercerKey]) && isBelowSelectedKey) {
            mercerData[mercerKey] = '';
            deleteCascadingFieldsIfNecessary(mercerKey, mercerData);
          }
        });
      })
    );
  }

  public setOtherMercerDataFieldsFor(data: MercerData, key: string, mercerData: MercerData): void {
    let matchingKeyPart = '';
    if (data) {
      if (key.includes('family')) {
        matchingKeyPart = 'family';
      } else if (key.includes('subFamily')) {
        matchingKeyPart = 'subFamily';
      } else if (key.includes('specialization')) {
        matchingKeyPart = 'specialization';
      } else if (key.includes('careerStream')) {
        matchingKeyPart = 'careerStream';
      } else if (key.includes('careerLevel')) {
        matchingKeyPart = 'careerLevel';
      }

      for (const dataKey of Object.keys(data)) {
        if (dataKey !== key && (!matchingKeyPart || dataKey.includes(matchingKeyPart))) {
          mercerData[dataKey] = data[dataKey];
        }
      }
    }
  }

  public async validateMercerUpwardsWithNewMercerData(
    selectedKey: string,
    value: string,
    mercerData: MercerData,
    isEqual = true
  ): Promise<void> {
    const validateMercer = new MercerData();
    validateMercer[selectedKey] = value;

    const data = await this.mercerDataService
      .getCorrelatingMercerData(validateMercer, selectedKey as MercerJobCodeSearchablePropertyEnum, isEqual)
      .toPromise();

    this.updateParentJobCategories(mercerData, data, selectedKey);
  }

  public validateMercerUpwardsWithCurrentMercerData(
    selectedKey: string,
    value: string,
    mercerData: MercerData,
    usePartialJobCode = false
  ): Observable<unknown> {
    mercerData[selectedKey] = value;
    return this.mercerDataService
      .getCorrelatingMercerData(mercerData, selectedKey as MercerJobCodeSearchablePropertyEnum, true, usePartialJobCode)
      .pipe(map(data => this.updateParentJobCategories(mercerData, data, selectedKey)));
  }

  private updateParentJobCategories(
    mercerData: MercerData,
    comparisonMercerData: MercerData,
    selectedKey: string
  ): void {
    for (const codeKey of Object.values(MercerJobCodeSearchablePropertyEnum)) {
      const titleKey = codeKey.replace('Code', '');
      const descriptionKey = `${titleKey}Description`;
      const breakLoop =
        selectedKey === MercerJobCodeSearchablePropertyEnum.CareerLevel
          ? codeKey === MercerJobCodeSearchablePropertyEnum.CareerLevelShort
          : titleKey === selectedKey;

      mercerData[codeKey] = comparisonMercerData[codeKey];
      mercerData[titleKey] = comparisonMercerData[titleKey];
      mercerData[descriptionKey] = comparisonMercerData[descriptionKey];

      // if same key is iterated, stop loop in order to not set job sections below changed selectedKey
      // but continue if career level is iterated because career level short has to be set
      if (breakLoop) {
        break;
      }
    }
  }

  public setJobCodeIfValid(data: MercerData, mercerData: MercerData): void {
    if (
      mercerData.familyCode &&
      mercerData.subFamilyCode &&
      mercerData.specializationCode &&
      mercerData.careerStream &&
      mercerData.careerLevelShort
    ) {
      mercerData.jobCode = data.jobCode;
      mercerData.jobTitle = data.jobTitle;
    } else {
      mercerData.jobCode = '';
      mercerData.jobTitle = '';
    }
  }
}
